import { slices } from "@/constants/sliceNames";
import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosIns from "@/axios/defaultAxios";
import blobAxios, { downloadBlob } from "@/axios/blobDownloadAxios";
import { serializeQueryParams } from "@/utils/utils";
import { DataSourceMonitorData } from "@/entities/DataSource/DataSourceMonitorData";
import { notification } from "antd";

import { errorMessage } from "../../actions/messages.actions";

const ENDPOINT = "/hoya/v3/classification/monitor";

/******* Get single data source details ********/

export const requestClassificationDataSourceMonitorData = createAsyncThunk(
  `${slices.datasource}/requestClassificationDataSourceMonitorData`,
  async ({ monitorId, params }, { rejectWithValue, dispatch, getState }) => {
    if (params.withoutLoading) {
      // preserve page by adding to page limit to refresh all the data
      const { itemsCurrentPage, itemsPageLimit } = getState()?.[slices.datasource];
      params["page_limit"] = itemsPageLimit * itemsCurrentPage;
    }
    const url = `${ENDPOINT}/${monitorId}/data?${serializeQueryParams(params)}`;
    try {
      const response = await axiosIns.get(url);
      const { data, ...rest } = response.data.data;
      const monitorData = DataSourceMonitorData.fromClassificationAPI(data);
      return { monitorData, ...rest };
    } catch (error) {
      if (error.response?.status !== 404) {
        dispatch(errorMessage(error.response?.data?.error ?? error.message));
      }
      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestClassificationDataSourceMonitorDataStatus = {
  [requestClassificationDataSourceMonitorData.fulfilled]: (state, action) => {
    const { monitorData, current_page, records_count, page_limit } = action.payload;
    state.dataLoading = false;
    state.itemData =
      current_page === 1
        ? monitorData
        : new DataSourceMonitorData(
            monitorData.getHeader(),
            [...state.itemData.rows, ...monitorData.rows],
            [...state.itemData.getImagesMetadata(), ...monitorData.getImagesMetadata()]
          );
    state.totalItemData = records_count;
    state.itemsCurrentPage = current_page;
    state.itemsPageLimit = page_limit;
  },
  [requestClassificationDataSourceMonitorData.pending]: (state, action) => {
    if (!action.meta?.arg?.params?.withoutLoading) {
      state.dataLoading = true;
    }
  },
  [requestClassificationDataSourceMonitorData.rejected]: (state, action) => {
    state.dataLoading = false;
    state.itemData = new DataSourceMonitorData();
    state.totalItemData = 1;
  },
};

/** Get monitor plate data */

export const requestClassificationMonitorPlateData = createAsyncThunk(
  `${slices.datasource}/requestClassificationMonitorPlateData`,
  async ({ id, dataId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.get(`${ENDPOINT}/${id}/data/${dataId}/images`);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestClassificationMonitorPlateDataStatus = {
  [requestClassificationMonitorPlateData.fulfilled]: (state, action) => {
    state.loading = false;
    state.plateImages = action.payload;
  },
  [requestClassificationMonitorPlateData.pending]: (state, action) => {
    state.loading = true;
  },
  [requestClassificationMonitorPlateData.rejected]: (state, action) => {
    state.loading = false;
    state.plateData = {};
  },
};

/******* Get datasource image details ********/

export const requestClassificationDataSourceImageDetails = createAsyncThunk(
  `${slices.datasource}/requestClassificationDataSourceImageDetails`,
  async ({ imageId, params }, { rejectWithValue, dispatch }) => {
    const url = `${ENDPOINT}/image-details/${imageId}`;
    try {
      const response = await axiosIns.get(url + "?" + serializeQueryParams(params));
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestClassificationDataSourceImageDetailsStatus = {
  [requestClassificationDataSourceImageDetails.fulfilled]: (state, action) => {
    state.imageDetails = action.payload;
    state.loading = false;
    state.error = "";
  },
  [requestClassificationDataSourceImageDetails.pending]: (state, action) => {
    state.loading = true;
    state.error = "";
  },
  [requestClassificationDataSourceImageDetails.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/** Get monitor labels list */
export const requestMonitorLabelsList = createAsyncThunk(
  `${slices.datasource}/requestMonitorLabelsList`,
  async ({ monitorId }, { rejectWithValue, dispatch }) => {
    const url = `${ENDPOINT}/${monitorId}/labels`;
    try {
      const response = await axiosIns.get(url);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestMonitorLabelsListStatus = {
  [requestMonitorLabelsList.fulfilled]: (state, action) => {
    state.labelsList = action.payload;
    state.labelsListLoading = false;
  },
  [requestMonitorLabelsList.pending]: (state, action) => {
    state.labelsListLoading = true;
  },
  [requestMonitorLabelsList.rejected]: (state, action) => {
    state.labelsListLoading = false;
  },
};

/** Edit image label */
export const requestEditImageLabel = createAsyncThunk(
  `${slices.datasource}/requestEditImageLabel`,
  async ({ monitorId, imageId, data }, { rejectWithValue, dispatch }) => {
    const url = `${ENDPOINT}/${monitorId}/images/${imageId}/relabel`;
    try {
      const response = await axiosIns.post(url, data);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestEditImageLabelStatus = {
  [requestEditImageLabel.fulfilled]: (state, action) => {
    state.loading = false;
  },
  [requestEditImageLabel.pending]: (state, action) => {
    state.loading = true;
  },
  [requestEditImageLabel.rejected]: (state, action) => {
    state.loading = false;
  },
};

/** Download monitor data relabeled results */
export const requestDownloadMonitorDataRelabeledResults = async (id) => {
  const url = `${ENDPOINT}/${id}/relabeled-images/download`;
  try {
    const response = await blobAxios.get(url);
    downloadBlob(response.data, "results.zip");
    return response;
  } catch (error) {
    notification.error({
      message: "Error!",
      description: error?.response?.data?.error || error.message,
    });
  }
};
